/**
 * SOLID Review
 * S — Strong. One concern: the frontend's Infrastructure boundary — its sockets, its lollipops, and the
 *     build step that turns the connected wire into the objects behind the lollipops.
 * O — Good. A new lollipop is one more part built in `build()` and one field on the provided holder.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Two holders and a build step.
 * D — Good. The two holders, this layer's own adapters, and the host's resources by constructor (the
 *     stores until FE6, the websocket's state, the consent platform) — typed by the Application, never
 *     the Framework.
 */
import { ComponentFrontendInfrastructureRequiredPorts } from './ComponentFrontendInfrastructureRequiredPorts';
import { ComponentFrontendInfrastructureProvidedPorts, type IConfigDepInfrastructureLollipops } from './ComponentFrontendInfrastructureProvidedPorts';
import type { IPortGraphQLDomainClient, IPortRestApiClient } from '../contract/ports';
import type { IServiceTableStore } from '../../application/state/gameStoreTypes';
import type { IServiceSessionStore } from '../../application/state/sessionStoreTypes';
import { ApolloSessionAdapter } from '../graphql/ApolloSessionAdapter';
import { createAuthApiAdapter } from '../api/authApiService';
import { ClientPlayerServerAdapter } from '../graphql/ClientPlayerServerAdapter';
import { ClientAuthServerAdapter } from '../api/ClientAuthServerAdapter';
import { ClientPaymentServerAdapter } from '../api/ClientPaymentServerAdapter';
import { ApolloPaymentAdapter } from '../graphql/ApolloPaymentAdapter';
import { ApolloAdsAdapter } from '../graphql/ApolloAdsAdapter';
import { ClientAdsServerAdapter } from '../graphql/ClientAdsServerAdapter';
import { ClientServerHealthAdapter } from '../health/ClientServerHealthAdapter';
import { ClientMediaServerAdapter } from '../livekit/ClientMediaServerAdapter';
import { ApolloLiveKitAdapter } from '../livekit/ApolloLiveKitAdapter';
import { LiveKitTokenService } from '../livekit/LiveKitTokenService';
import { ClientLegalServerAdapter } from '../legal/ClientLegalServerAdapter';
import { getSessionToken } from '../storage/sessionTokenStorage';
import { ClientTabStorageAdapter } from '../storage/ClientTabStorageAdapter';
import { ApolloGameAdapter } from '../graphql/ApolloGameAdapter';
import { ApolloRoomAdapter } from '../graphql/ApolloRoomAdapter';
import { ClientStateStore } from '../state/ClientStateStore';
import { ClientRoundServerAdapter } from '../graphql/ClientRoundServerAdapter';
import { ClientRoomServerAdapter } from '../graphql/ClientRoomServerAdapter';
import { ClientServerWatchAdapter, type IConfigDepServerWatch } from '../events/ClientServerWatchAdapter';
import { ClientConsentPlatformAdapter, type IConfigDepConsentPlatform } from '../consent/ClientConsentPlatformAdapter';
import type { IGatewayConsent } from '../../application/consent/IGatewayConsent';

/** Drops `readonly` for `build()`'s one assignment — see there. */
type TBuildable = { -readonly [K in keyof Pick<ComponentFrontendInfrastructure, 'providedPorts'>]: ComponentFrontendInfrastructure[K] };

/**
 * What the host hands Infrastructure (rule 2.3: host resources are constructor arguments, not sockets).
 *
 *   - `tableStore`: the zustand game store, which `ClientState` and `ClientStateView` wrap. It moves into
 *     this layer in FE6, when nothing else writes it.
 *   - `sessionStore`: the auth store (FE2) — the session's account, screen and pending join, read and
 *     written through the same two ports. Since FE3 it holds no token: the JWT lives in the token holder
 *     (`infrastructure/storage/sessionTokenStorage.ts`), read only by this layer.
 *   - `connection`: the websocket's state changes (`wsConnectionMonitor`), delivered as events.
 *   - `consentGateway`: the consent management platform (Google Funding Choices, FE4b) — browser APIs,
 *     main-thread only; its changes are delivered as events too.
 *   - `healthUrl`: the server's /health, which the health probe fetches (FE5a).
 */
export interface IConfigDepInfrastructureHost {
  readonly tableStore: IServiceTableStore;
  readonly sessionStore: IServiceSessionStore;
  readonly connection: {
    onStateChange(handler: (state: string) => void): () => void;
    /** Re-open the socket as the seated player when the stored seat changes (FE9, §28.206). */
    reidentify(): void;
  };
  readonly consentGateway: IGatewayConsent;
  /** Where the server's /health answers — env and page location, resolved by the host (FE5a). */
  readonly healthUrl: string;
}

/** The two wire transport clients the composition root fills the eight wire sockets with. */
export interface IConfigDepContractClients {
  readonly graphql: IPortGraphQLDomainClient;
  readonly rest: IPortRestApiClient;
}

/**
 * THE FRONTEND'S INFRASTRUCTURE AS A COMPONENT (frontend strict-ports plan, FE1d, 2026-10-03), shaped
 * exactly like the backend's `ComponentInfrastructure`: `MODEL_NAME`, two holders, and the lifecycle
 * construct → the composition root connects every socket → `build()` → the lollipops exist.
 *
 * It no longer connects itself. `connect()` and `fill()` filled the eight wire sockets from inside
 * the class (§28.77's "a component connects its own ports when it can do so through abstractions").
 * They are deleted. `FrontendConnectors` connects every socket of every frontend component, with the
 * port observer, as the backend's one connector table does (overturns §28.77; flagged in FE1's §28).
 */
export class ComponentFrontendInfrastructure {
  /** The model name in every generated diagram — one literal, so a typo cannot split this box in two. */
  static readonly MODEL_NAME = 'ComponentFrontendInfrastructure';

  readonly requiredPorts = new ComponentFrontendInfrastructureRequiredPorts();
  /**
   * ⚠ ASSIGNED IN `build()`, unlike `requiredPorts`: the objects behind these lollipops wrap adapters
   * that read the wire sockets, which are connected one phase after construction. Until then the field
   * is undefined, and `describePorts` walks nothing for it.
   */
  readonly providedPorts!: ComponentFrontendInfrastructureProvidedPorts;

  constructor(private readonly host: IConfigDepInfrastructureHost) {}

  /**
   * Phase 3: read the connected sockets and build what the lollipops carry. The round and room adapters
   * are the same Apollo adapters the legacy Framework sockets carry, constructed over THIS component's
   * domain sockets.
   */
  build(): void {
    const sockets = this.requiredPorts;
    const game = new ApolloGameAdapter(sockets.graphQLRoundDomainSocket.getInterface());
    const rooms = new ApolloRoomAdapter(sockets.graphQLRoomDomainSocket.getInterface());
    const state = new ClientStateStore(this.host.tableStore, this.host.sessionStore);
    const events = sockets.clientServerEventsSocket.getInterface();
    const accounts = new ApolloPaymentAdapter(sockets.graphQLPaymentDomainSocket.getInterface());
    const streams: IConfigDepServerWatch = { rooms, rounds: game, accounts, connection: this.host.connection, events };
    const authRest = createAuthApiAdapter(sockets.restAuthApiSocket.getInterface());
    const mediaDomain = sockets.graphQLMediaDomainSocket.getInterface();
    const platform: IConfigDepConsentPlatform = { gateway: this.host.consentGateway, events };
    const parts: IConfigDepInfrastructureLollipops = {
      state,
      view: state,
      rounds: new ClientRoundServerAdapter(game),
      rooms: new ClientRoomServerAdapter(rooms),
      watch: new ClientServerWatchAdapter(streams),
      tabStorage: new ClientTabStorageAdapter(() => this.host.connection.reidentify()),
      players: new ClientPlayerServerAdapter(new ApolloSessionAdapter(sockets.graphQLPlayerDomainSocket.getInterface())),
      auth: new ClientAuthServerAdapter(authRest),
      payments: new ClientPaymentServerAdapter(authRest),
      ads: new ClientAdsServerAdapter(new ApolloAdsAdapter(sockets.graphQLAdsDomainSocket.getInterface())),
      health: new ClientServerHealthAdapter(this.host.healthUrl),
      media: new ClientMediaServerAdapter(new ApolloLiveKitAdapter(mediaDomain, new LiveKitTokenService(mediaDomain))),
      legal: new ClientLegalServerAdapter(getSessionToken, sockets.restLegalApiSocket.getInterface()),
      consent: new ClientConsentPlatformAdapter(platform),
    };
    // The fields are `readonly`, which TypeScript permits assigning only in the constructor — exactly
    // where this cannot happen. One narrowly typed alias, the backend's pattern for the same phasing.
    (this as TBuildable).providedPorts = new ComponentFrontendInfrastructureProvidedPorts(parts);
  }
}
